type Bag = Record<string, unknown>

export interface HeapEvent {
  event_properties?: Bag
  session_properties?: Bag
  pageview_properties?: Bag
}

/** Class on the clicked element itself whose text is safe to record. */
export const CAPTURE_ALLOW_CLASS = 'capture-allow'

// Static segments under app/(authenticated); rewrite.test.ts fails on drift.
export const ROUTE_SEGMENTS: ReadonlySet<string> = new Set(
  'add billing companies contracts csv-upload dependents dev documents employees organization quotes renewals renewals-dev-harness roster settings upload'.split(
    ' ',
  ),
)
// A fixed set: any tag-shaped token could be forged text from an attribute value.
const KNOWN_TAGS = new Set(
  'a article aside b body button caption circle code details dialog div em fieldset footer form g h1 h2 h3 h4 h5 h6 header i img input label legend li main nav ol option p path section select small span strong summary svg table tbody td textarea tfoot th thead tr u ul'.split(
    ' ',
  ),
)

// Keys not listed here are dropped, so a field Heap adds later fails closed.
// `title` on an event is its name (click, pageViewed), not the page title.
const EVENT_KEYS = [
  'id',
  'title',
  'time',
  'z',
  'sch',
  'scw',
  'ubv',
  'upv',
  'umo',
  'target_tag',
  'custom',
]
const PAGEVIEW_KEYS = ['domain', 'time', 'sch', 'scw']
const SESSION_KEYS = ['domain', 'time']

function isBag(value: unknown): value is Bag {
  return typeof value === 'object' && value !== null
}

function pick(source: Bag, keys: string[]): Bag {
  const picked: Bag = {}
  for (const key of keys) if (key in source) picked[key] = source[key]
  return picked
}

// Only known route segments survive; any other value becomes a placeholder.
function templatePath(path: unknown): string {
  if (typeof path !== 'string') return ''
  const [pathOnly] = path.split(/[?#]/, 1)
  return pathOnly
    .split('/')
    .map(segment =>
      segment === '' || ROUTE_SEGMENTS.has(segment) ? segment : '[id]',
    )
    .join('/')
}

function templateHref(href: unknown): string {
  if (typeof href !== 'string') return ''
  try {
    return templatePath(new URL(href, 'http://placeholder').pathname)
  } catch {
    return ''
  }
}

// Heap does not escape values, so `|` or `;` in one can forge elements.
function tagsOnly(hierarchy: unknown): string {
  if (typeof hierarchy !== 'string') return ''
  return hierarchy
    .split('|')
    .map(element => element.split(';', 1)[0])
    .filter(
      element => element.startsWith('@') && KNOWN_TAGS.has(element.slice(1)),
    )
    .map(element => `${element}|`)
    .join('')
}

// Unlike the hierarchy string, target_class cannot be forged by attribute values.
function isAllowlisted(event: Bag): boolean {
  const classes = event.target_class
  return (
    typeof classes === 'string' &&
    classes.split(/\s+/).includes(CAPTURE_ALLOW_CLASS)
  )
}

function rewritePageView(view: Bag): Bag {
  const previous = view.previous_page
  return {
    ...pick(view, PAGEVIEW_KEYS),
    path: templatePath(view.path),
    title: '',
    previous_page: isBag(previous) ? { path: templatePath(previous.path) } : {},
  }
}

function rewriteSession(session: Bag): Bag {
  return { ...pick(session, SESSION_KEYS), path: templatePath(session.path) }
}

function rewriteEvent(event: Bag): Bag {
  const rewritten = pick(event, EVENT_KEYS)
  if ('href' in event) rewritten.href = templateHref(event.href)
  if ('target_id' in event) rewritten.target_id = ''
  if ('hierarchy' in event) rewritten.hierarchy = tagsOnly(event.hierarchy)
  if ('target_text' in event) {
    rewritten.target_text = isAllowlisted(event) ? event.target_text : ''
  }
  return rewritten
}

/**
 * Heap's `config.rewrite` hook. A throw makes Heap send the raw event, so every
 * failure returns null, which drops it.
 */
export function rewriteHeapEvent(input: unknown): HeapEvent | null {
  try {
    if (!isBag(input)) return null
    const result: HeapEvent = {}
    if (isBag(input.event_properties)) {
      result.event_properties = rewriteEvent(input.event_properties)
    }
    if (isBag(input.session_properties)) {
      result.session_properties = rewriteSession(input.session_properties)
    }
    if (isBag(input.pageview_properties)) {
      result.pageview_properties = rewritePageView(input.pageview_properties)
    }
    return Object.keys(result).length > 0 ? result : null
  } catch {
    return null
  }
}
